import { normalizeDurationInputValue } from "../../../../../../../../../../../../../../../_shared/_logic/time-granularity"
import { formatDateOnly } from "../../../../../../../../../../../../../_shared/_logic/dates"
import {
  catalogOrderTypeIds,
  isCustomOrderTypeSelection,
  orderTypeSelectionValues,
  reconcileOrderTypeSelection,
  summedTypicalDurationMinutes,
} from "../../../../../../../../../../../../../_shared/_logic/order-type-selection"
import {
  KEEP_PRECONFIGURATION_VALUE,
  isKeepPreconfigurationChoice,
  reconcileKeepPreconfigurationSelection,
  withKeepPreconfigurationOption,
  type PreconfigurationChoice,
} from "../../../../../../../../../../../../../_shared/_logic/preconfiguration-keep"
import type {
  WorkOrderCreationFields,
  WorkOrderCreationFieldsPatch,
} from "../../../../../_shared/_logic/work-order-creation"
import type { PlanningDueDatePreset } from "../../../../../_shared/_logic/planning-due-date-preset"

/** Suggested appointment durations in minutes; other values are entered individually. */
export const DURATION_PRESET_MINUTES = [5, 10, 15, 20, 25, 30, 45, 60, 120] as const
export const CUSTOM_DURATION_SELECTION = "custom"

type DueDatePresetOption = { value: PlanningDueDatePreset; dueDate: string | null }

/**
 * Select state of the batch order preconfiguration. Every field either keeps the row value or
 * carries the concrete value applied to all target rows.
 */
export type OrderFieldsPreconfiguration = {
  /** Multi-select values: only "keep", catalog ids, or only the free-text marker. */
  orderTypeValues: string[]
  customOrderTypeLabel: string
  /** "keep", a preset minute value, or the individual-entry marker. */
  duration: string
  customDurationMinutes: string
  /** Multi-select values: only "keep" or employee ids. */
  allowedEmployeeIds: string[]
  dueDatePreset: PreconfigurationChoice<PlanningDueDatePreset>
  /** Resolved deadline of the chosen preset or the individually entered date. */
  dueDate: string
}

export const durationPreconfigurationOptions = withKeepPreconfigurationOption([
  ...DURATION_PRESET_MINUTES.map((minutes) => ({
    label: `${minutes} Minuten`,
    value: String(minutes),
  })),
  { label: "Individuell erfassen", value: CUSTOM_DURATION_SELECTION },
])

const durationChoice = (
  minutes: string,
): Pick<OrderFieldsPreconfiguration, "duration" | "customDurationMinutes"> => {
  const normalized = normalizeDurationInputValue(minutes)

  return DURATION_PRESET_MINUTES.some((preset) => String(preset) === normalized)
    ? { duration: normalized, customDurationMinutes: "" }
    : { duration: CUSTOM_DURATION_SELECTION, customDurationMinutes: normalized }
}

const dueDateChoice = (
  dueDate: string,
  presets: readonly DueDatePresetOption[],
): Pick<OrderFieldsPreconfiguration, "dueDatePreset" | "dueDate"> => ({
  dueDatePreset:
    presets.find((option) => option.dueDate && option.dueDate === dueDate)?.value ?? "custom",
  dueDate,
})

/** Concrete preconfiguration that reproduces the given row fields when applied. */
export const orderFieldsPreconfigurationFromFields = (
  fields: WorkOrderCreationFields,
  dueDatePresets: readonly DueDatePresetOption[],
): OrderFieldsPreconfiguration => {
  const orderTypeValues = orderTypeSelectionValues(fields)

  return {
    orderTypeValues: orderTypeValues.length ? orderTypeValues : [KEEP_PRECONFIGURATION_VALUE],
    customOrderTypeLabel: fields.custom_order_type?.internal_label ?? "",
    ...durationChoice(fields.estimated_duration_min),
    allowedEmployeeIds: fields.allowed_employee_ids.length
      ? [...fields.allowed_employee_ids]
      : [KEEP_PRECONFIGURATION_VALUE],
    ...dueDateChoice(fields.due_date, dueDatePresets),
  }
}

/**
 * Applies a changed order type multi-select. Concrete catalog types preselect their summed typical
 * duration; "keep" also keeps the durations, because they belong to the rows' own types.
 */
export const changeOrderTypePreconfiguration = (
  value: OrderFieldsPreconfiguration,
  incoming: readonly string[],
  orderTypes: readonly OrderType[],
): OrderFieldsPreconfiguration => {
  const withoutKeep = reconcileKeepPreconfigurationSelection(value.orderTypeValues, incoming)

  if (withoutKeep.includes(KEEP_PRECONFIGURATION_VALUE)) {
    return {
      ...value,
      orderTypeValues: withoutKeep,
      customOrderTypeLabel: "",
      duration: KEEP_PRECONFIGURATION_VALUE,
      customDurationMinutes: "",
    }
  }

  const previousValues = value.orderTypeValues.filter(
    (entry) => !isKeepPreconfigurationChoice(entry),
  )
  const orderTypeValues = reconcileOrderTypeSelection(previousValues, withoutKeep)

  if (isCustomOrderTypeSelection(orderTypeValues)) return { ...value, orderTypeValues }

  return {
    ...value,
    orderTypeValues,
    customOrderTypeLabel: "",
    ...durationChoice(String(summedTypicalDurationMinutes(orderTypeValues, orderTypes) ?? "")),
  }
}

export const changeDueDatePreconfiguration = (
  value: OrderFieldsPreconfiguration,
  preset: string,
  presets: readonly DueDatePresetOption[],
): OrderFieldsPreconfiguration => {
  if (isKeepPreconfigurationChoice(preset)) return { ...value, dueDatePreset: preset }

  const option = presets.find((entry) => entry.value === preset)

  if (!option) return value

  return { ...value, dueDatePreset: option.value, dueDate: option.dueDate ?? value.dueDate }
}

/** Row fields the preconfiguration sets; fields on "keep" are omitted. */
export const orderFieldsPatchFromPreconfiguration = (
  value: OrderFieldsPreconfiguration,
): WorkOrderCreationFieldsPatch => {
  const patch: WorkOrderCreationFieldsPatch = {}

  if (!value.orderTypeValues.includes(KEEP_PRECONFIGURATION_VALUE)) {
    const label = value.customOrderTypeLabel.trim()
    patch.orderTypeSelection = isCustomOrderTypeSelection(value.orderTypeValues)
      ? { order_type_ids: [], custom_order_type: { internal_label: label, public_label: label } }
      : { order_type_ids: catalogOrderTypeIds(value.orderTypeValues), custom_order_type: null }
  }

  if (!isKeepPreconfigurationChoice(value.duration)) {
    patch.estimated_duration_min = normalizeDurationInputValue(
      value.duration === CUSTOM_DURATION_SELECTION ? value.customDurationMinutes : value.duration,
    )
  }

  if (!value.allowedEmployeeIds.includes(KEEP_PRECONFIGURATION_VALUE)) {
    patch.allowed_employee_ids = [...value.allowedEmployeeIds]
  }

  if (!isKeepPreconfigurationChoice(value.dueDatePreset)) {
    patch.due_date = formatDateOnly(value.dueDate)
  }

  return patch
}
